export interface ColorOut {
  key: string;
  name: string;
  emoji_id: number;
  rgb: [number, number, number, number];
}

export interface TemplateOut {
  id: string;
  name: string;
  description: string | null;
  icon_id: number | null;
  preview_url: string;
  max_name_length: number;
  text_align: "right" | "center" | "left";
  default_stroke: Record<string, unknown> | null;
  default_outline: Record<string, unknown> | null;
  default_start_fraction: number;
  default_end_fraction: number;
  is_premium: boolean;
  colors: ColorOut[];
  is_favorited: boolean;
}

export interface TemplatesPage {
  items: TemplateOut[];
  page: number;
  total_pages: number;
  total: number;
}

export interface TextPosition {
  x: number;
  y: number;
  rotation: number;
  scale: number;
}

export interface FontOut {
  id: string;
  label: string;
}

export interface FontsListResponse {
  fonts: FontOut[];
}

export interface IconOut {
  id: string;
  file: string;
  label: string;
  char: string;
}

export interface IconsListResponse {
  icons: IconOut[];
}

export interface TextItem {
  id: string;
  text: string;
  is_icon: boolean;
  text_color: string | null;
  stroke_color: string | null;
  stroke_width: number | null;
  stroke_opacity: number | null; // 0..100
  stroke_line_cap: number | null; // 1=Butt, 2=Round, 3=Square
  stroke_line_join: number | null; // 1=Miter, 2=Round, 3=Bevel
  outline_enabled: boolean | null; // null = follow the template's own default; true/false = explicit override
  outline_color: string | null; // "#RRGGBB" - the 3D-effect offset copy's color
  outline_offset_x: number | null;
  outline_offset_y: number | null;
  font_size: number | null;
  font_id: string | null;
  position: TextPosition;
  start_fraction: number; // 0..1 - when this text starts being visible
  end_fraction: number; // 0..1 - when this text stops being visible
  gradient_color: string | null; // "#RRGGBB" - null means flat text_color, no gradient
  curve_amount: number; // -100..100, 0 = straight line - independent of gradient, both can be active together
  stretch_x: number; // 0.25..4, 1 = no stretch - independent horizontal stretch, multiplies into zoom
  stretch_y: number; // 0.25..4, 1 = no stretch - independent vertical stretch, multiplies into zoom
  z_order: "front" | "back" | null; // null = follow the template's own default
  shine_style: "sweep" | "shimmer" | null; // null = no shine effect
  shine_color: string | null; // "#RRGGBB" - null = default per style (white for sweep, gold for shimmer)
  motion_style: "fade" | "slide_up" | "slide_left" | "slide_right" | "zoom_in" | "pop" | "spin" | "drop" | "float" | "pulse" | "swing" | "shake" | "bounce" | "fade_out" | "zoom_out" | "neon_glow" | "flicker" | "typewriter" | "wave" | "explode" | null; // حركة جاهزة للنص، null = بدون
  reveal_style: "draw" | "marker" | "neon" | null; // الاسم يُرسم بالقلم ثم يمتلئ
  reveal_dir?: "auto" | "ltr" | "rtl" | "center" | "top" | "bottom";
  reveal_mode?: "letters" | "wave" | "together";
  reveal_speed?: number; // 0.5..2.5
  reveal_tip?: "none" | "dot" | "pencil" | "spark" | "star";
  reveal_trail?: boolean;
  reveal_fx?: "none" | "sparks" | "stardust";
  reveal_fill?: "fade" | "pop" | "rise";
  reveal_keep?: boolean;
  reveal_out?: "none" | "fade" | "erase";
}

export type EffectType =
  | "stars" | "sparkles" | "hearts" | "flowers" | "confetti" | "fireworks" | "explosion" | "fire"
  | "smoke" | "snow" | "rain" | "bubbles" | "lightning" | "ripple" | "fireflies";

export interface EffectItem {
  id: string;
  effect_type: EffectType;
  color: string;
  intensity?: number; // كثافة الجزيئات 0.4..2.5
  speed?: number; // 0.4..2.5
  size?: number; // حجم الجزيئات 0.5..2
  seed?: number; // توزيع عشوائي ثابت
  position: TextPosition;
  start_fraction: number;
  end_fraction: number;
}

export type PenType = "regular" | "marker" | "brush" | "neon";

export const PEN_STROKE_WIDTH: Record<PenType, number> = {
  regular: 6, // "قلم عادي" - medium
  marker: 3, // "قلم خط" - thin, precise
  brush: 14, // "فرشاة" - thick, soft
  neon: 10, // "نيون" - VIP-exclusive, glowing double-stroke
};

// "neon" is VIP-only - the basic 3 pens and normal color picker stay
// free for everyone. Kept as one shared source of truth so the
// frontend (what to show as locked) and backend (what to actually
// reject) can never drift out of sync with each other.
export const VIP_EXCLUSIVE_PENS: PenType[] = ["neon"];

export interface DrawingStroke {
  id: string;
  pen_type: PenType;
  color: string; // hex
  points: [number, number][]; // sticker-space coordinates (0-512 canvas)
}

export interface DesignState {
  template_id: string;
  texts: TextItem[];
  effects: EffectItem[];
  drawings: DrawingStroke[];
  updated_at?: number | null;
}

let drawingIdCounter = 0;
export const newDrawingId = (): string => `drawing-${Date.now()}-${drawingIdCounter++}`;

let textIdCounter = 0;
export const newTextId = (): string => `text-${Date.now()}-${textIdCounter++}`;

let effectIdCounter = 0;
export const newEffectId = (): string => `effect-${Date.now()}-${effectIdCounter++}`;

export const emptyPosition = (): TextPosition => ({ x: 0, y: 0, rotation: 0, scale: 1 });

export const emptyTextItem = (template?: TemplateOut): TextItem => ({
  id: newTextId(),
  text: "",
  is_icon: false,
  text_color: null,
  stroke_color: null,
  stroke_width: null,
  stroke_opacity: null,
  stroke_line_cap: null,
  stroke_line_join: null,
  outline_enabled: null,
  outline_color: null,
  outline_offset_x: null,
  outline_offset_y: null,
  font_size: null,
  font_id: null,
  position: emptyPosition(),
  // Matches the template's own configured text_timing by default (e.g.
  // "starts at 1:30") instead of always claiming the full 0-100% - the
  // user can still drag the slider to change it from there.
  start_fraction: template?.default_start_fraction ?? 0,
  end_fraction: template?.default_end_fraction ?? 1,
  gradient_color: null,
  curve_amount: 0,
  stretch_x: 1,
  stretch_y: 1,
  z_order: null,
  shine_style: null,
  shine_color: null,
  motion_style: null,
  reveal_style: null,
  reveal_dir: "auto",
  reveal_mode: "letters",
  reveal_speed: 1,
  reveal_tip: "dot",
  reveal_trail: false,
  reveal_fx: "none",
  reveal_fill: "fade",
  reveal_keep: false,
  reveal_out: "none",
});

export const iconTextItem = (icon: IconOut, template?: TemplateOut): TextItem => ({
  ...emptyTextItem(template),
  text: icon.char,
  is_icon: true,
  font_id: icon.file,
});

export const EFFECT_DEFAULT_COLORS: Record<EffectType, string> = {
  stars: "#ffd700",
  sparkles: "#fff1a8",
  hearts: "#ff3b5c",
  flowers: "#ff6fae",
  confetti: "#ff7a00",
  fireworks: "#ffb347",
  explosion: "#ff7a00",
  fire: "#ff5a1f",
  smoke: "#b0b0b0",
  snow: "#e6f2ff",
  rain: "#7fb2ff",
  bubbles: "#7fd8ff",
  lightning: "#8c9bff",
  ripple: "#3ec7ff",
  fireflies: "#d9ff3f",
};

export const EFFECT_LABELS: Record<EffectType, string> = {
  stars: "نجوم تدور",
  sparkles: "بريق ولمعان",
  hearts: "قلوب طايرة",
  flowers: "ورود وأزهار",
  confetti: "قصاصات احتفال",
  fireworks: "ألعاب نارية",
  explosion: "انفجار",
  fire: "نار",
  smoke: "دخان",
  snow: "ثلج",
  rain: "مطر",
  bubbles: "فقاعات",
  lightning: "برق ورعد",
  ripple: "موجات",
  fireflies: "يراعات",
};

export const emptyEffectItem = (effectType: EffectType): EffectItem => ({
  id: newEffectId(),
  effect_type: effectType,
  color: EFFECT_DEFAULT_COLORS[effectType],
  intensity: 1,
  speed: 1,
  size: 1,
  seed: Math.floor(Math.random() * 999000) + 1,
  position: emptyPosition(),
  start_fraction: 0,
  end_fraction: 1,
});

export const MAX_TEXT_ITEMS = 6;
export const MAX_EFFECT_ITEMS = 6;

export const emptyDesign = (template: TemplateOut): DesignState => ({
  template_id: template.id,
  texts: [emptyTextItem(template)],
  effects: [],
  drawings: [],
});

export interface RenderPreviewResponse {
  lottie_json: Record<string, unknown>;
  width: number;
  height: number;
}

export interface RenderFinalizeResponse {
  sticker_id: string;
  name: string;
  preview_lottie_json: Record<string, unknown>;
}

export interface PackSummary {
  name: string;
  title: string;
  sticker_count: number;
}

export interface PackListResponse {
  packs: PackSummary[];
}

export interface PackActionResponse {
  ok: boolean;
  pack_name: string;
  pack_title: string;
  pack_link?: string | null;
  sticker_count: number;
  error?: string | null;
}

export type MembershipTier = "free" | "pro" | "vip";

export interface MembershipOut {
  tier: MembershipTier;
  expires_at: number | null;
}

export interface AccountOut {
  user_id: number;
  first_name: string;
  username: string | null;
  photo_url: string | null;
  membership: MembershipOut;
  packs_count: number;
  stickers_count: number;
  daily_used: number;
  daily_limit: number | null; // null = unlimited (vip)
  updates_channel_link: string;
  tutorial_video_url: string;
}

export interface BannerOut {
  slot: number;
  image_url: string;
  link: string;
}

export interface BannersResponse {
  banners: BannerOut[];
}

export interface FolderOut {
  id: string; // "all" for the synthesized "show everything" folder
  name: string;
  image_url: string;
  template_count: number;
}

export interface FoldersResponse {
  folders: FolderOut[];
}

export interface BulkStickerResult {
  name: string;
  ok: boolean;
  error?: string | null;
}

export interface BulkRenderResponse {
  pack_name: string;
  pack_title: string;
  pack_link?: string | null;
  results: BulkStickerResult[];
  succeeded_count: number;
  failed_count: number;
}
